// CameraCapture.js - UX MEJORADA: Cierre temprano con primer token
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Fade,
  Slide
} from '@mui/material';
import { Camera, X, RotateCcw, Check, Sparkles } from 'lucide-react';
import agtfitCoachAssistantService from '../service/agtfitCoachAssistantService';

const CameraCapture = ({ open, onClose, onImageAnalyzed }) => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [analysisProgress, setAnalysisProgress] = useState(''); // ✅ NUEVO: Mostrar progreso
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Detener la cámara
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Iniciar la cámara
  const initCamera = useCallback(async () => {
    try {
      stopCamera();
      setCapturedImage(null);

      console.log('Iniciando cámara...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error al iniciar la cámara:', error);
    }
  }, [stopCamera]);

  // Iniciar/detener la cámara cuando cambia el estado de open
  useEffect(() => {
    if (open) {
      initCamera();
      // Mostrar guía por 4 segundos
      const timer = setTimeout(() => setShowGuide(false), 4000);
      return () => clearTimeout(timer);
    }

    return () => {
      stopCamera();
    };
  }, [open, initCamera, stopCamera]);

  // Resetear estados cuando se abre el modal
  useEffect(() => {
    if (open) {
      setCapturedImage(null);
      setIsAnalyzing(false);
      setShowGuide(true);
      setAnalysisProgress(''); // ✅ NUEVO: Reset progreso
    }
  }, [open]);

  // Reducir tamaño de imagen manteniendo proporciones
  const resizeImage = (imageDataUrl, maxWidth = 800, maxHeight = 600, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageDataUrl;
      img.onload = () => {
        let newWidth = img.width;
        let newHeight = img.height;
        
        if (newWidth > maxWidth) {
          newHeight = (newHeight * maxWidth) / newWidth;
          newWidth = maxWidth;
        }
        
        if (newHeight > maxHeight) {
          newWidth = (newWidth * maxHeight) / newHeight;
          newHeight = maxHeight;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        const resizedImageDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        console.log(`Tamaño imagen original: ${Math.round(imageDataUrl.length / 1024)} KB`);
        console.log(`Tamaño imagen comprimida: ${Math.round(resizedImageDataUrl.length / 1024)} KB`);
        
        resolve(resizedImageDataUrl);
      };
    });
  };

  // Capturar imagen
  const captureImage = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.7);
    
    const resizedImage = await resizeImage(imageDataUrl, 800, 600, 0.6);
    setCapturedImage(resizedImage);

    stopCamera();
  };

  // ✅ NUEVO: Analizar imagen con cierre temprano mejorado
  const analyzeImage = async () => {
    if (!capturedImage) return;
    
    setIsAnalyzing(true);
    setAnalysisProgress('Conectando con el asistente...');
    
    try {
      console.log('🚀🖼️ Iniciando análisis de imagen con cierre temprano...');
      
      const query = "Analiza esta imagen de comida. Identifica los alimentos, estima las calorías y macronutrientes (proteínas, carbohidratos y grasas). Si es una tabla nutricional, extrae la información relevante. Proporciona la información en formato estructurado y ofrece registrar estos alimentos.";
      
      let analysisResult = '';
      let hasReceivedFirstToken = false; // ✅ FLAG para primer token
      let tokenCount = 0;
      
      await agtfitCoachAssistantService.sendImageQueryStream(
        query,
        capturedImage,
        [], // conversationHistory vacío
        
        // ✅ MEJORA: onToken con cierre temprano
        (token) => {
          tokenCount++;
          analysisResult += token;
          
          console.log(`🔤 Token #${tokenCount} recibido:`, token.substring(0, 20) + '...');
          
          // ✅ CERRAR MODAL CON EL PRIMER TOKEN SIGNIFICATIVO
          if (!hasReceivedFirstToken && token.trim().length > 0) {
            hasReceivedFirstToken = true;
            console.log('✨ Primer token recibido - Cerrando modal y mostrando en chat');
            
            // Cerrar modal inmediatamente
            setIsAnalyzing(false);
            onClose();
            
            // ✅ ENVIAR IMAGEN Y TEXTO INICIAL AL CHAT
            onImageAnalyzed(capturedImage, token, true); // ✅ NUEVO: flag isStreaming = true
          } else if (hasReceivedFirstToken) {
            // ✅ CONTINUAR ENVIANDO TOKENS AL CHAT
            onImageAnalyzed(capturedImage, token, false); // ✅ NUEVO: flag isStreaming = false (solo update)
          }
        },
        
        // ✅ MEJORA: onComplete simplificado
        (response) => {
          console.log(`✅ Análisis completado con ${tokenCount} tokens`);
          
          // Si por alguna razón no se cerró antes, cerrar ahora
          if (!hasReceivedFirstToken) {
            setIsAnalyzing(false);
            onClose();
            onImageAnalyzed(capturedImage, analysisResult || "Análisis completado");
          } else {
            // Solo notificar que terminó (el chat ya está mostrando el contenido)
            onImageAnalyzed(capturedImage, '', 'complete'); // ✅ NUEVO: señal de completado
          }
        },
        
        // ✅ MEJORA: onError con mejor manejo
        (error) => {
          console.error('❌ Error en análisis de imagen:', error);
          setIsAnalyzing(false);
          onClose();
          onImageAnalyzed(capturedImage, `Lo siento, no pude analizar correctamente la imagen: ${error.message}. Por favor intenta de nuevo o describe manualmente lo que ves en la imagen.`);
        }
      );
      
    } catch (error) {
      console.error('Error al analizar la imagen:', error);
      setIsAnalyzing(false);
      onClose();
      onImageAnalyzed(capturedImage, "Lo siento, no pude analizar correctamente la imagen. Por favor intenta de nuevo o describe manualmente lo que ves en la imagen.");
    }
  };

  // Reiniciar cámara
  const retakePhoto = () => {
    setCapturedImage(null);
    setAnalysisProgress('');
    initCamera();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen
      TransitionComponent={Slide}
      TransitionProps={{
        direction: "up",
        timeout: 400
      }}
      PaperProps={{
        sx: {
          overflow: 'hidden',
          margin: 0,
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          backgroundColor: '#000'
        }
      }}
    >
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
      }}>
        
        {/* Header con gradiente sutil */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
          zIndex: 9,
          pointerEvents: 'none'
        }} />

        {/* Botón cerrar con estilo iOS */}
        <IconButton 
          onClick={onClose} 
          sx={{
            position: 'absolute',
            top: { xs: '16px', sm: '20px' },
            right: { xs: '16px', sm: '20px' },
            zIndex: 10,
            width: '36px',
            height: '36px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(20px)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.8)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <X size={20} />
        </IconButton>

        {!capturedImage ? (
          <>
            {/* Video con overlay suave */}
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />

            {/* Guía visual para enfocar comida */}
            <Fade in={showGuide} timeout={800}>
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '280px',
                height: '280px',
                border: '3px solid rgba(255, 152, 0, 0.8)',
                borderRadius: '24px',
                background: 'rgba(255, 152, 0, 0.05)',
                backdropFilter: 'blur(2px)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                animation: 'pulse 2s infinite'
              }}>
                <Typography variant="body2" sx={{ 
                  color: 'white', 
                  textAlign: 'center',
                  fontWeight: 500,
                  fontSize: '16px',
                  textShadow: '0 2px 8px rgba(0,0,0,0.7)'
                }}>
                  Centra la comida en el marco
                </Typography>
              </Box>
            </Fade>

            {/* Instrucciones en la parte superior */}
            <Fade in={showGuide} timeout={1000}>
              <Box sx={{
                position: 'absolute',
                top: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 4,
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ 
                  color: 'white', 
                  fontWeight: 600,
                  fontSize: '20px',
                  textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                  mb: 1
                }}>
                  Análisis Nutricional
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  fontWeight: 400,
                  fontSize: '14px',
                  textShadow: '0 1px 8px rgba(0,0,0,0.8)'
                }}>
                  Captura tu comida para análisis automático
                </Typography>
              </Box>
            </Fade>

            {/* Botón de captura con estilo premium */}
            <Box sx={{
              position: 'absolute',
              bottom: { xs: '40px', sm: '60px' },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 5
            }}>
              <Box sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Anillo exterior animado */}
                <Box sx={{
                  position: 'absolute',
                  width: '90px',
                  height: '90px',
                  borderRadius: '50%',
                  border: '3px solid rgba(255, 152, 0, 0.3)',
                  animation: 'breathe 3s ease-in-out infinite'
                }} />
                
                {/* Botón principal */}
                <IconButton 
                  onClick={captureImage}
                  sx={{
                    width: '70px',
                    height: '70px',
                    background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 50%, #FF8A00 100%)',
                    color: 'white',
                    border: '4px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.4), inset 0 2px 0 rgba(255,255,255,0.2)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 12px 40px rgba(255, 152, 0, 0.6), inset 0 2px 0 rgba(255,255,255,0.3)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    }
                  }}
                >
                  <Camera size={32} />
                </IconButton>
              </Box>
            </Box>

            {/* Footer con gradiente */}
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '160px',
              background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
              zIndex: 2,
              pointerEvents: 'none'
            }} />
          </>
        ) : (
          <>
            {/* Imagen capturada */}
            <Fade in={!!capturedImage} timeout={600}>
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                position: 'absolute',
                top: 0,
                left: 0
              }}>
                <img
                  src={capturedImage}
                  alt="Captured preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Box>
            </Fade>
            
            {/* ✅ MEJORA: Overlay de análisis con progreso más informativo */}
            <Fade in={isAnalyzing} timeout={400}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'radial-gradient(circle, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.8) 100%)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 6
              }}>
                <Box sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <CircularProgress 
                    size={80} 
                    thickness={3}
                    sx={{
                      color: '#FF9800',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      }
                    }}
                  />
                  <Sparkles 
                    size={32} 
                    color="#FF9800" 
                    style={{
                      position: 'absolute',
                      animation: 'sparkle 2s ease-in-out infinite'
                    }}
                  />
                </Box>
                
                <Typography variant="h5" sx={{ 
                  color: 'white', 
                  fontWeight: 600,
                  fontSize: '24px',
                  mb: 1,
                  textAlign: 'center'
                }}>
                  Analizando imagen
                </Typography>
                
                {/* ✅ NUEVO: Mostrar progreso específico */}
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  fontWeight: 400,
                  fontSize: '16px',
                  textAlign: 'center',
                  minHeight: '24px' // Evitar saltos de layout
                }}>
                  {analysisProgress || 'Identificando alimentos y nutrientes...'}
                </Typography>
                
                {/* ✅ NUEVO: Mensaje de cierre temprano */}
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.5)', 
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  mt: 2,
                  fontStyle: 'italic'
                }}>
                  El análisis aparecerá en tu chat en unos momentos
                </Typography>
              </Box>
            </Fade>
            
            {/* Botones de acción mejorados */}
            <Fade in={!!capturedImage && !isAnalyzing} timeout={600}>
              <Box sx={{
                position: 'absolute',
                bottom: '60px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 4,
                zIndex: 5
              }}>
                {/* Botón reintentar */}
                <IconButton 
                  onClick={retakePhoto}
                  disabled={isAnalyzing}
                  sx={{
                    width: '70px',
                    height: '70px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(20px)',
                    border: '3px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    }
                  }}
                >
                  <RotateCcw size={28} />
                </IconButton>
                
                {/* Botón confirmar */}
                <IconButton
                  onClick={analyzeImage}
                  disabled={isAnalyzing}
                  sx={{
                    width: '70px',
                    height: '70px',
                    background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 50%, #FF8A00 100%)',
                    color: 'white',
                    border: '3px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.4), inset 0 2px 0 rgba(255,255,255,0.2)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 12px 40px rgba(255, 152, 0, 0.6)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                    '&:disabled': {
                      opacity: 0.6,
                      transform: 'none',
                    }
                  }}
                >
                  <Check size={28} />
                </IconButton>
              </Box>
            </Fade>
          </>
        )}
        
        {/* Canvas oculto */}
        <canvas 
          ref={canvasRef} 
          style={{ 
            display: 'none',
            position: 'absolute',
            top: 0,
            left: 0
          }} 
        />

        {/* Estilos CSS adicionales */}
        <style jsx>{`
          @keyframes pulse {
            0%, 100% { 
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.8;
            }
            50% { 
              transform: translate(-50%, -50%) scale(1.05);
              opacity: 1;
            }
          }
          
          @keyframes breathe {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.6;
            }
            50% { 
              transform: scale(1.1);
              opacity: 1;
            }
          }
          
          @keyframes sparkle {
            0%, 100% { 
              transform: rotate(0deg) scale(1);
              opacity: 0.8;
            }
            25% { 
              transform: rotate(90deg) scale(1.1);
              opacity: 1;
            }
            50% { 
              transform: rotate(180deg) scale(0.9);
              opacity: 0.6;
            }
            75% { 
              transform: rotate(270deg) scale(1.1);
              opacity: 1;
            }
          }
        `}</style>
      </Box>
    </Dialog>
  );
};

export default CameraCapture;